import { useState, useEffect, useMemo } from "react";
import { loadIneligibleUsersAsync } from "@/api/assignments";
import type { IneligibleUsersResponse, StoredUser } from "@/api/assignments";

const EMPTY: IneligibleUsersResponse = { ineligibleUsers: [] };

/**
 * Users in the selected audience who can't be auto-enrolled in the selected path
 * because the path's level sits above their current level.
 */
export function useIneligibleUsers(
  roleId: string | undefined,
  users: StoredUser[] | undefined
): { data: IneligibleUsersResponse; isLoading: boolean } {
  const [data, setData] = useState<IneligibleUsersResponse>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);

  // Sorted uuid string keeps the effect stable across re-renders of the same audience.
  const uuidKey = useMemo(
    () =>
      (users ?? [])
        .map(u => u.directoryUuid)
        .filter((uuid): uuid is string => !!uuid)
        .sort()
        .join(","),
    [users]
  );

  useEffect(() => {
    if (!roleId || !uuidKey) {
      setData(EMPTY);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    loadIneligibleUsersAsync(roleId, uuidKey.split(","))
      .then(response => {
        if (!cancelled) setData(response);
      })
      .catch(err => {
        if (cancelled) return;
        setData(EMPTY);
        console.error("Failed to load ineligible users:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roleId, uuidKey]);

  return { data, isLoading };
}
